import { type ReactNode, createContext, useContext, useState } from 'react';

import { useDialogRouterState } from 'src/modules/use-dialog-router-state';
import { ConfirmableDrawer } from 'src/ui/ConfirmableDrawer/ConfirmableDrawer';

interface EditorDrawerContext {
  isDrawerOpen: boolean;
  onEditorOpen: (id?: string) => void;
  editingId: string | null;
  onEditorClose: () => void;
  isFormDirty: boolean;
  onFormDirty: (isFormDirty: boolean) => void;
  switchToListView: () => void;
}

const context = createContext<EditorDrawerContext>({
  isDrawerOpen: false,
  onEditorOpen: () => {},
  editingId: null,
  onEditorClose: () => {},
  isFormDirty: false,
  onFormDirty: () => {},
  switchToListView: () => {},
});

interface Props {
  children: React.ReactNode;
  name: string;
}

export function EditorDrawerProvider({ children, name }: Props) {
  const [isFormDirty, setIsFormDirty] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const {
    open: addHash,
    close: removeHash,
    isOpen,
  } = useDialogRouterState(name);

  const switchToListView = () => {
    setEditingId(null);
    setIsFormDirty(false);
  };

  return (
    <context.Provider
      value={{
        isFormDirty,
        onFormDirty: setIsFormDirty,
        editingId,
        isDrawerOpen: isOpen,
        onEditorOpen: (id?: string) => {
          if (!isOpen) {
            addHash();
          }
          if (id) {
            setEditingId(id);
          }
        },
        onEditorClose: () => {
          if (isOpen) {
            removeHash();
          }
          switchToListView();
        },
        switchToListView,
      }}
    >
      {children}
    </context.Provider>
  );
}

export function useEditorDrawer() {
  const values = useContext(context);

  if (!values) {
    throw new Error(
      'useDrawerEditor must be used within a DrawerEditorProvider',
    );
  }

  return values;
}

export function EditorDrawer({
  title,
  name,
  onClose,
  children,
}: {
  title: string;
  name: string;
  onClose?: () => void;
  children: ReactNode;
}) {
  const parentContext = useEditorDrawer();
  const { onEditorClose, isFormDirty, isDrawerOpen } = parentContext;

  const handleClose = () => {
    onEditorClose();
    onClose?.();
  };

  return (
    <ConfirmableDrawer
      name={name}
      width="small"
      title={title}
      open={isDrawerOpen}
      shouldConfirmBeforeClose={isFormDirty}
      onClose={handleClose}
    >
      {children}
    </ConfirmableDrawer>
  );
}
